import { isNullOrUndefined, isUndefined } from "@silo/utils";
import { ReactNode, createContext, useContext } from "react";
import { useSiloWeb3 } from "./SiloWeb3Provider";
import {
  ChecksumAddress,
  ClientProvider,
  ProtocolInstance,
  SupportedChainId,
  isSupportedChainId,
} from "@silo/types";

export interface SiloWeb3Connection {
  clientProvider: ClientProvider;
  chainId: SupportedChainId;
  protocolInstance: ProtocolInstance;
  account?: ChecksumAddress;
}

const SiloWeb3ConnectionContext = createContext<SiloWeb3Connection>(null!);

export const useSiloWeb3Connection = () => {
  const context = useContext(SiloWeb3ConnectionContext);

  if (isNullOrUndefined(context)) {
    throw new Error(
      "useSiloWeb3Connection must be used within a SiloWeb3ConnectionBoundary"
    );
  }

  return context;
};
interface SiloWeb3ConnectionBoundaryProps {
  fallback?: ReactNode;
  children?: ReactNode;
}

/**
 * Only renders children if there is an active web3 connection. There may or may not be a connected user.
 *
 * Use the `useSiloWeb3Connection` hook to access the connection.
 */
export function SiloWeb3ConnectionBoundary({
  fallback = null,
  children,
}: SiloWeb3ConnectionBoundaryProps): JSX.Element | null {
  const { clientProvider, chainId, account, protocolInstance } = useSiloWeb3();

  if (
    isNullOrUndefined(clientProvider) ||
    !isSupportedChainId(chainId) ||
    isUndefined(protocolInstance)
  ) {
    return fallback ? <>{fallback}</> : null;
  }

  return (
    <SiloWeb3ConnectionContext.Provider
      value={{
        clientProvider: clientProvider,
        chainId,
        account,
        protocolInstance,
      }}
    >
      {children}
    </SiloWeb3ConnectionContext.Provider>
  );
}
